import { logStartUsage } from 'owa-analytics-start';
import { owaDefaultAccountPersistenceIdPrefix } from 'owa-account-source-list-types';
import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import { escapePersistenceId } from './escapePersistenceId';
import tryGetGlobalSettingsAccountMailboxInfo from 'owa-account-source-list-store/lib/selectors/tryGetGlobalSettingsAccountMailboxInfo';

/**
 * There can be multiple active OWA tabs and we need to make sure that we build a stable
 * persistence id for OWA accounts, i.e. consistent for the same account across tab contexts.
 * We use the user's puid and tenant guid, or email address (when puid and tenant id is not available),
 * except in the case of explicit logon where we add in the mailbox guid (otherwise multiple tabs
 * accessing the same shared mailbox with different authentication contexts would get the same persistence id).
 * @param userConfiguration The owa user configuration associated with the default account
 */
function getPersistenceIdForDefaultAccountImpl(userConfiguration: OwaUserConfiguration): string {
    const usersession = userConfiguration?.SessionSettings;
    const tenant = usersession?.ExternalDirectoryTenantGuid;
    const puid = usersession?.UserPuid;
    const mailboxGuid = usersession?.MailboxGuid;
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * We are getting the persistend if for the default account, this happens during startup and we need to use the passed in userConfiguration. */
    if (!!userConfiguration?.SessionSettings?.IsExplicitLogon) {
        if (!!tenant && !!mailboxGuid && !!puid) {
            return `${owaDefaultAccountPersistenceIdPrefix}-${mailboxGuid}+${puid}@${tenant}`;
        }

        // We did not get the suffix using the PUID and tenant guid, we want to log that
        // Using tryGetGlobalSettingsAccountMailboxInfo: This is early-boot infrastructure
        // telemetry about persistence ID construction. No account mailbox exists yet.
        logStartUsage(
            'InitOwaAccount_MissingMbxGuidOrTenantGuid',
            tryGetGlobalSettingsAccountMailboxInfo(false),
            {
                tenantType: typeof tenant,
                mbxGuidType: typeof mailboxGuid,
                puidType: typeof puid,
            }
        );
    }

    // Because we are in OWA we know we only connect to M365 sources and can used the tenant
    // guid and user PUID to identity the account. This will be stable with the possible exception
    // of a cloud cache account that is deleted and recreated, which should be infrequent and
    // we can tolerate those id changing sometimes.
    if (!!tenant && !!puid) {
        return `${owaDefaultAccountPersistenceIdPrefix}-${puid}@${tenant}`;
    }

    // Fallback to using the user email address
    const userEmailAddress = usersession?.UserEmailAddress;
    const hasSuffix = !!userEmailAddress;

    // We did not get the suffix using the PUID and tenant guid, we want to log that
    // Using tryGetGlobalSettingsAccountMailboxInfo: This is early-boot infrastructure
    // telemetry about persistence ID construction. No account mailbox exists yet.
    logStartUsage(
        'InitOwaAccount_MissingPuidOrTenantGuid',
        tryGetGlobalSettingsAccountMailboxInfo(false),
        {
            hasSuffix,
            tenantType: typeof tenant,
            puidType: typeof puid,
            emailType: typeof userEmailAddress,
        }
    );

    return hasSuffix
        ? `${owaDefaultAccountPersistenceIdPrefix}-${userEmailAddress}`
        : owaDefaultAccountPersistenceIdPrefix;
}

export function getPersistenceIdForDefaultAccount(userConfiguration: OwaUserConfiguration): string {
    const original = getPersistenceIdForDefaultAccountImpl(userConfiguration);
    const escaped = escapePersistenceId(original);
    if (original != escaped) {
        // Using tryGetGlobalSettingsAccountMailboxInfo: This is early-boot infrastructure
        // telemetry about persistence ID escaping. No account mailbox exists yet.
        logStartUsage(
            'InitOwaAccount_PersistenceId_Escaped',
            tryGetGlobalSettingsAccountMailboxInfo(false)
        );
    }

    return escaped;
}
